import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { StoreUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { loadAnalytics } from 'store/actions/analyticsActions';
import {
  ServiceLineSelect,
  LoadingView,
  Paginateable,
  DynamicTable,
} from 'components';
import { ColorUtil } from 'utils';

const styles = (theme) => ({
  card: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  titleLabelName: {
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  claimsDateDropDown: {
    width: 160,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  collapseDropDown: {
    width: 200,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  filterLabel: {
    backgroundColor: 'white',
    paddingRight: 4,
  },
  select: {
    '& .MuiMenuItem-root.Mui-selected, .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: '#00a887',
      color: 'white',
    },
  },
  // Multiselect menu: keep selected rows visually unchanged (only the checkbox
  // reflects selection).
  collapseMenu: {
    '& .MuiMenuItem-root.Mui-selected': {
      backgroundColor: 'transparent',
    },
  },
  notLabelledItem: {
    fontStyle: 'italic',
  },
  rollUpLabelLink: {
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  // List (mirrors the market-data provider list)
  tableContainer: {
    width: '100%',
    fontSize: 12,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#EBF1F8',
    borderRadius: '4px 4px 0 0',
    padding: '8px 0',
    marginBottom: 2,
    width: '100%',
  },
  headerCell: {
    color: '#64748B',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: '0 8px',
    whiteSpace: 'nowrap',
  },
  dataRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderRadius: 2,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#F8FAFC',
    },
  },
  cell: {
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconCol: {
    width: '2%',
    minWidth: 28,
    display: 'flex',
    justifyContent: 'center',
  },
  nameCol: {
    width: '20%',
    minWidth: 120,
  },
  specialtyCol: {
    width: '15%',
    minWidth: 80,
  },
  orgCol: {
    width: '20%',
    minWidth: 100,
  },
  labelCol: {
    width: '12%',
    minWidth: 80,
  },
  patientsCol: {
    width: 20,
    minWidth: 20,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: 0,
  },
  pctCol: {
    width: 48,
    minWidth: 48,
    flexShrink: 0,
    textAlign: 'left',
    paddingLeft: 8,
    fontSize: 11,
    fontWeight: 400,
    color: '#4B5563',
  },
  chartCol: {
    flex: 1,
    minWidth: 150,
    paddingRight: 12,
  },
  // Spans both the patient-icon column (110px) and the bar column so the
  // "Shared Patients" header sits above both.
  sharedHeaderCol: {
    flex: 1,
    minWidth: 178,
  },
  providerName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  collapsedRow: {
    backgroundColor: '#EEF6F4',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#E3F0EC' },
    '&:nth-of-type(even)': { backgroundColor: '#EEF6F4' },
  },
  collapsedName: {
    fontWeight: 700,
    color: '#0B7A63',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  collapsedIcon: {
    fontSize: 16,
    color: '#00a887',
  },
  specialty: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  orgName: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  affiliatedIcon: {
    fontSize: 14,
    color: '#5391D5',
  },
  patientIcon: {
    fontSize: 12,
    color: '#9CA3AF',
    marginRight: 4,
  },
  patientCount: {
    fontSize: 11,
    fontWeight: 400,
    color: '#4B5563',
  },
  barContainer: {
    width: '100%',
    height: 18,
    backgroundColor: 'transparent',
    borderRadius: 2,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.5s ease-in-out',
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#64748B',
    fontSize: 14,
    fontWeight: 300,
  },
  processing: {
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  processingText: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: 14,
    fontWeight: 300,
    maxWidth: 420,
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  // Hover popup listing connected providers within the label
  tipPaper: {
    maxWidth: 'none',
    backgroundColor: '#fff',
    color: '#212529',
    border: '1px solid #E2E8F0',
    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)',
    padding: 0,
  },
  tipTable: {
    fontSize: 11,
    minWidth: 360,
    maxWidth: 480,
  },
  tipTitle: {
    padding: '10px 12px 6px',
    fontSize: 14,
    fontWeight: 700,
    color: '#0B7A63',
  },
  tipHeaderRow: {
    display: 'flex',
    borderBottom: '1px solid #E2E8F0',
    padding: '4px 10px',
    fontSize: 10,
    fontWeight: 600,
    color: '#64748B',
    textTransform: 'uppercase',
  },
  tipRow: {
    display: 'flex',
    padding: '4px 10px',
    '&:nth-of-type(even)': { backgroundColor: '#F8FAFC' },
  },
  tipName: {
    width: '40%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: 6,
    textTransform: 'uppercase',
  },
  tipOrg: {
    width: '42%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: 6,
    color: '#4B5563',
  },
  tipCount: {
    width: '18%',
    textAlign: 'right',
  },
  tipMessage: {
    padding: '10px',
    color: '#64748B',
    fontSize: 11,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  dialogTitleText: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.primary.main,
    lineHeight: 1.3,
  },
  dialogLink: {
    display: 'inline-block',
    marginBottom: 12,
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: 600,
    color: '#5391D5',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
});

// Click detail dialog: providers in the current label that share patients with
// the clicked provider, in the opposite direction from the page. Fetched
// lazily when the dialog opens and cached per provider/direction/serviceline.
const DIALOG_COLUMNS = [
  { label: 'Provider', key: 'name', sortable: false },
  {
    label: 'Organization',
    key: 'organization',
    sortable: false,
    format: (v) => v || '-',
  },
  {
    label: 'Shared Patients',
    key: 'shared_patients',
    sortable: false,
    format: (v) => (v || 0).toLocaleString(),
  },
];

function ProviderSharedDialog(props) {
  const { classes, open, onClose, info, query, onQueryChange } = props;
  const dispatch = useDispatch();
  const storeId = info ? info.storeId : null;
  const result = useSelector((state) =>
    storeId ? StoreUtil.get(state.analytics, storeId) : null,
  );

  useEffect(() => {
    if (open && info && storeId) {
      dispatch(
        loadAnalytics({
          type: 'LABEL_PROVIDER_SHARED',
          store_id: storeId,
          filter: info.filter,
          opts: { limit: query.limit, offset: query.offset },
        }),
      );
    }
  }, [open, storeId, query.limit, query.offset, dispatch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <span className={classes.dialogTitleText}>
          {info && (
            <>
              Providers in{' '}
              <span className={classes.titleLabelName}>{info.labelName}</span>{' '}
              that see patients {info.oppositeLabel}{' '}
              <span className={classes.titleLabelName}>
                {info.providerName}
              </span>
            </>
          )}
        </span>
        <IconButton onClick={onClose} size='small' aria-label='close'>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {info && (
          <a
            className={classes.dialogLink}
            href={`/providers/${info.providerId}/market`}
          >
            Go To {info.providerName} Market Data
          </a>
        )}
        <DynamicTable
          columns={DIALOG_COLUMNS}
          data={result}
          query={query}
          onQueryChange={onQueryChange}
          idKey='provider_id'
          getRowHref={(row) => `/providers/${row.provider_id}/market`}
          ContainerType={React.Fragment}
          showFilters={false}
        />
      </DialogContent>
    </Dialog>
  );
}

ProviderSharedDialog.propTypes = {
  classes: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  info: PropTypes.object,
  query: PropTypes.object.isRequired,
  onQueryChange: PropTypes.func.isRequired,
};

function LabelPatientFlow(props) {
  const { tag, classes } = props;
  const dispatch = useDispatch();

  const selectedClient = useSelector((state) => state.app.selectedClient);
  const flow = useSelector((state) =>
    StoreUtil.get(state.analytics, 'LABEL_PATIENT_FLOW'),
  );

  const [direction, setDirection] = useState('after');
  const [serviceline, setServiceline] = useState('all');
  // Labels (by id) to collapse into a single summary row. Defaults to the
  // current label.
  const [collapse, setCollapse] = useState(() => (tag?.id ? [tag.id] : []));
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInfo, setDialogInfo] = useState(null);
  const [dialogQuery, setDialogQuery] = useState({ limit: 10, offset: 0 });

  const closeDialog = useCallback(() => setDialogOpen(false), []);

  useEffect(() => {
    dispatch(
      loadAnalytics({
        type: 'LABEL_PATIENT_FLOW',
        filter: {
          client_id: selectedClient,
          tag_id: tag.id,
          direction,
          serviceline,
          collapse,
        },
        opts: {
          limit,
          offset,
        },
      }),
    );
  }, [tag.id, direction, serviceline, collapse, limit, offset, selectedClient]);

  // Filter changes reset to the first page.
  const onDirectionChange = useCallback((event) => {
    setDirection(event.target.value);
    setOffset(0);
  }, []);

  const onServicelineChange = useCallback((id) => {
    setServiceline(id);
    setOffset(0);
  }, []);

  const onCollapseChange = useCallback((event) => {
    const value = event.target.value;
    setCollapse(
      typeof value === 'string' ? value.split(',').map(Number) : value,
    );
    setOffset(0);
  }, []);

  const onQueryChange = useCallback((query) => {
    setLimit(query.limit);
    setOffset(query.offset);
  }, []);

  // Clicking a rolled-up row un-rolls it (removes that label from the Roll Up).
  const onExpandCollapsed = useCallback((labelId) => {
    const key = labelId == null ? 0 : labelId;
    setCollapse((prev) => prev.filter((id) => id !== key));
    setOffset(0);
  }, []);

  // Clicking a provider's label name rolls that label up (adds it to Roll Up).
  const onRollUpLabel = useCallback((labelId) => {
    const key = labelId == null ? 0 : labelId;
    setCollapse((prev) => (prev.indexOf(key) > -1 ? prev : [...prev, key]));
    setOffset(0);
  }, []);

  const result = StoreUtil.getData(flow) ?? {};
  const data = result.rows ?? [];
  const collapsed = result.collapsed ?? [];
  const totalShared = result.totalShared || 0;
  const processing = !!result.processing;
  const loading = StoreUtil.isLoading(flow) || !StoreUtil.isLoaded(flow);

  // Assign each label in the group a stable color so bars/labels are colored
  // consistently across pages and filters.
  const groupLabels = result.labels ?? [];
  const colorByLabel = useMemo(() => {
    const palette = ColorUtil.getColors(Math.max(groupLabels.length, 1));
    const map = {};
    groupLabels.forEach((l, i) => {
      map[l.id] = palette[i % palette.length];
    });
    return map;
  }, [groupLabels]);
  const NEUTRAL_BAR = '#CBD5E1';

  // When every label (plus "Not Labelled") is rolled up there are no individual
  // provider rows, so hide the Specialty / Organization columns.
  const showProviderCols = !(
    groupLabels.length > 0 && collapse.length === groupLabels.length + 1
  );

  // Collapsed rows (pinned at top) are scaled among themselves; provider rows
  // among themselves -- so a big collapsed row doesn't flatten the providers.
  const { rows, maxValue, maxCollapsed } = useMemo(() => {
    let maxP = 0;
    let maxC = 0;
    const all = [...collapsed, ...data];
    const mapped = all.map((row) => {
      const total = parseInt(row.shared_patients, 10) || 0;
      if (row.type === 'collapsed') {
        maxC = Math.max(maxC, total);
      } else {
        maxP = Math.max(maxP, total);
      }
      return { ...row, total };
    });
    return { rows: mapped, maxValue: maxP || 1, maxCollapsed: maxC || 1 };
  }, [data, collapsed]);

  return (
    <Card className={classes.card}>
      <CardContent>
        <div className={classes.titleRow}>
          <Typography component='h2' variant='h6' color='primary'>
            Providers who see patients
          </Typography>
          <FormControl
            variant='outlined'
            className={classes.claimsDateDropDown}
            key='claimsdate'
          >
            <InputLabel shrink className={classes.filterLabel}>
              Claims Date
            </InputLabel>
            <Select
              value={direction}
              onChange={onDirectionChange}
              label='Claims Date'
              notched
              MenuProps={{ PaperProps: { className: classes.select } }}
            >
              <MenuItem value='after'>After</MenuItem>
              <MenuItem value='before'>Before</MenuItem>
            </Select>
          </FormControl>
          <Typography component='h2' variant='h6' color='primary'>
            Providers in{' '}
            <span className={classes.titleLabelName}>{tag?.name ?? ''}</span>
          </Typography>
        </div>

        <div className={classes.filters}>
          <ServiceLineSelect
            value={serviceline}
            onChange={onServicelineChange}
            showSubServicelines
            showAll
          />
          <FormControl
            variant='outlined'
            className={classes.collapseDropDown}
            key='collapse'
          >
            <InputLabel shrink className={classes.filterLabel}>
              Roll Up
            </InputLabel>
            <Select
              multiple
              value={collapse}
              onChange={onCollapseChange}
              label='Roll Up'
              notched
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return <em>None</em>;
                }
                // Everything (all labels + "Not Labelled") selected -> "All".
                if (
                  groupLabels.length > 0 &&
                  selected.length === groupLabels.length + 1
                ) {
                  return 'All';
                }
                return selected
                  .map((id) =>
                    id === 0
                      ? 'Not Labelled'
                      : groupLabels.find((l) => l.id === id)?.name ||
                        (id === tag?.id ? tag?.name : id),
                  )
                  .join(', ');
              }}
              MenuProps={{ PaperProps: { className: classes.collapseMenu } }}
            >
              {groupLabels.map((l) => (
                <MenuItem value={l.id} key={l.id}>
                  <Checkbox
                    size='small'
                    checked={collapse.indexOf(l.id) > -1}
                  />
                  <ListItemText primary={l.name} />
                </MenuItem>
              ))}
              <MenuItem value={0} key='not-labelled'>
                <Checkbox size='small' checked={collapse.indexOf(0) > -1} />
                <ListItemText
                  primary='Not Labelled'
                  primaryTypographyProps={{
                    className: classes.notLabelledItem,
                  }}
                />
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        {loading && <LoadingView />}

        {!loading && processing && (
          <div className={classes.processing}>
            <CircularProgress size={36} thickness={4} />
            <div className={classes.processingText}>
              Patient flow data is currently being processed. Results will be
              available once processing completes.
            </div>
          </div>
        )}

        {!loading && !processing && rows.length === 0 && (
          <div className={classes.empty}>
            No patient flow data for the providers in this label with the
            current filters.
          </div>
        )}

        {!loading && !processing && rows.length > 0 && (
          <div className={classes.tableContainer}>
            <div className={classes.headerRow}>
              <div className={`${classes.headerCell} ${classes.iconCol}`} />
              <div className={`${classes.headerCell} ${classes.nameCol}`}>
                Provider
              </div>
              {showProviderCols && (
                <div className={`${classes.headerCell} ${classes.specialtyCol}`}>
                  Specialty
                </div>
              )}
              {showProviderCols && (
                <div className={`${classes.headerCell} ${classes.orgCol}`}>
                  Organization
                </div>
              )}
              <div className={`${classes.headerCell} ${classes.labelCol}`}>
                Label
              </div>
              <div className={`${classes.headerCell} ${classes.sharedHeaderCol}`}>
                Shared Patients
              </div>
            </div>

            {rows.map((row, index) => {
              const barScale =
                row.type === 'collapsed' ? maxCollapsed : maxValue;
              const barWidth = row.total > 0 ? (row.total / barScale) * 100 : 0;
              // Share of the total shared patients in this view. Individual
              // provider rows show 2 decimals; rolled-up rows are coarser.
              const pct = totalShared > 0 ? (row.total / totalShared) * 100 : 0;
              let pctLabel = '';
              if (totalShared > 0) {
                pctLabel =
                  row.type === 'collapsed'
                    ? `${pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%`
                    : `${pct.toFixed(2)}%`;
              }

              // Collapsed label-group row (no individual provider).
              if (row.type === 'collapsed') {
                const label = row.label_name || 'Not Labelled';
                const providerCount = row.provider_count;
                // The "Not Labelled" group (null label) is always grey; others
                // use their assigned color.
                const isNotLabelled = row.label_id == null;
                const barColor = isNotLabelled
                  ? NEUTRAL_BAR
                  : colorByLabel[row.label_id] || '#00a887';
                const textColor = isNotLabelled
                  ? '#64748B'
                  : colorByLabel[row.label_id];
                const colorStyle = textColor ? { color: textColor } : undefined;
                return (
                  <div
                    key={`collapsed:${row.label_id ?? 'none'}:${index}`}
                    className={`${classes.dataRow} ${classes.collapsedRow}`}
                    onClick={() => onExpandCollapsed(row.label_id)}
                    title='Click to expand'
                  >
                    <div className={`${classes.cell} ${classes.iconCol}`}>
                      <Icon
                        className={classes.collapsedIcon}
                        style={colorStyle}
                      >
                        sell
                      </Icon>
                    </div>
                    <div className={`${classes.cell} ${classes.nameCol}`}>
                      <span
                        className={classes.collapsedName}
                        style={{ ...colorStyle, textTransform: 'none' }}
                      >
                        {providerCount != null
                          ? `${providerCount.toLocaleString()} provider${providerCount === 1 ? '' : 's'}`
                          : label}
                      </span>
                    </div>
                    {showProviderCols && (
                      <div className={`${classes.cell} ${classes.specialtyCol}`} />
                    )}
                    {showProviderCols && (
                      <div className={`${classes.cell} ${classes.orgCol}`} />
                    )}
                    <div className={`${classes.cell} ${classes.labelCol}`}>
                      <span
                        className={classes.collapsedName}
                        style={{ ...colorStyle, textTransform: 'none' }}
                      >
                        {label}
                      </span>
                    </div>
                    <div className={`${classes.cell} ${classes.patientsCol}`}>
                      <Icon className={classes.patientIcon}>group</Icon>
                    </div>
                    <div className={`${classes.cell} ${classes.pctCol}`}>
                      {pctLabel}
                    </div>
                    <div className={`${classes.cell} ${classes.chartCol}`}>
                      <div className={classes.barContainer}>
                        <div
                          className={classes.bar}
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: barColor,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              const provider = row.provider || {};
              const orgName = provider.location?.organization?.name || '';
              const specialtyName = provider.taxonomy
                ? SpecialtyUtil.getNameFromTaxonomy(provider.taxonomy)
                : '';
              // Color by the label this provider belongs to within the group.
              const providerLabelColor = colorByLabel[row.provider_label?.id];
              const barColor = providerLabelColor || NEUTRAL_BAR;

              const oppositeLabel = direction === 'after' ? 'before' : 'after';
              const providerName = `${provider.last_name || ''}, ${provider.first_name || ''}`;
              const dialogData = {
                providerId: row.provider_id,
                providerName,
                labelName: tag?.name || 'this label',
                oppositeLabel,
                storeId: `LABEL_PROVIDER_SHARED:${row.provider_id}:${direction}:${serviceline}`,
                filter: {
                  client_id: selectedClient,
                  tag_id: tag.id,
                  provider_id: row.provider_id,
                  direction,
                  serviceline,
                },
              };

              return (
                  <ButtonBase
                    key={`flow:${row.provider_id}`}
                    className={classes.dataRow}
                    onClick={() => {
                      setDialogInfo(dialogData);
                      setDialogQuery({ limit: 10, offset: 0 });
                      setDialogOpen(true);
                    }}
                  >
                  <div className={`${classes.cell} ${classes.iconCol}`}>
                    {provider.is_affiliated && (
                      <Tooltip
                        placement='bottom-start'
                        title={
                          <Typography className={classes.infoTip}>
                            Affiliated Provider
                          </Typography>
                        }
                      >
                        <Icon className={classes.affiliatedIcon}>hub</Icon>
                      </Tooltip>
                    )}
                  </div>
                  <div className={`${classes.cell} ${classes.nameCol}`}>
                    <span className={classes.providerName}>
                      {`${provider.last_name || ''}, ${provider.first_name || ''}`}
                    </span>
                  </div>
                  {showProviderCols && (
                    <div className={`${classes.cell} ${classes.specialtyCol}`}>
                      <span className={classes.specialty}>
                        {specialtyName || '-'}
                      </span>
                    </div>
                  )}
                  {showProviderCols && (
                    <div className={`${classes.cell} ${classes.orgCol}`}>
                      <span className={classes.orgName}>{orgName || '-'}</span>
                    </div>
                  )}
                  <div className={`${classes.cell} ${classes.labelCol}`}>
                    <span
                      className={`${classes.orgName} ${classes.rollUpLabelLink}`}
                      style={
                        providerLabelColor
                          ? { color: providerLabelColor, fontWeight: 600 }
                          : undefined
                      }
                      title='Click to roll up this label'
                      onClick={(e) => {
                        e.stopPropagation();
                        onRollUpLabel(row.provider_label?.id);
                      }}
                    >
                      {row.provider_label?.name || 'Not Labelled'}
                    </span>
                  </div>
                  <div className={`${classes.cell} ${classes.patientsCol}`}>
                    <Icon className={classes.patientIcon}>person</Icon>
                  </div>
                  <div className={`${classes.cell} ${classes.pctCol}`}>
                    {pctLabel}
                  </div>
                  <div className={`${classes.cell} ${classes.chartCol}`}>
                    <div className={classes.barContainer}>
                      <div
                        className={classes.bar}
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: barColor,
                        }}
                      />
                    </div>
                  </div>
                  </ButtonBase>
              );
            })}

            <Paginateable
              data={flow}
              query={{ limit, offset }}
              onQueryChange={onQueryChange}
            />
          </div>
        )}
      </CardContent>
      <ProviderSharedDialog
        classes={classes}
        open={dialogOpen}
        onClose={closeDialog}
        info={dialogInfo}
        query={dialogQuery}
        onQueryChange={setDialogQuery}
      />
    </Card>
  );
}

LabelPatientFlow.propTypes = {
  classes: PropTypes.object,
  tag: PropTypes.object.isRequired,
};

const styled = withStyles(styles)(LabelPatientFlow);
export { styled as LabelPatientFlow };
